/**
 * Authentication Provider Component
 * Wraps the app and provides authentication state
 */
import React from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { Spinner, Text } from "@fluentui/react-components";

import { loginRequest } from "./authConfig";
import { useUsageTracking } from "../hooks/useUsageTracking";
import { useDevBypass } from "../utils/devBypass";

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Loading screen shown during authentication
 */
const AuthLoading: React.FC = () => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      gap: "16px",
    }}
  >
    <Spinner size="large" />
    <Text size={400}>Signing you in...</Text>
  </div>
);

/**
 * Login prompt for unauthenticated users
 */
const LoginPrompt: React.FC = () => {
  const { instance } = useMsal();

  const handleLogin = () => {
    instance.loginRedirect(loginRequest);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        gap: "24px",
        padding: "20px",
        background: "linear-gradient(135deg, #004C97 0%, #003a75 50%, #2D2926 100%)",
      }}
    >
      {/* LensLock Logo with Tagline */}
      <div style={{
        backgroundColor: "#ffffff",
        padding: "24px 32px",
        borderRadius: "12px",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
      }}>
        <img 
          src="/lenslock_logo_color_tagline.png" 
          alt="LensLock - We Serve Heroes Every Day" 
          style={{ height: "120px", width: "auto" }}
        />
      </div>
      <Text size={600} weight="semibold" style={{ color: "#ffffff", fontFamily: "'Poppins', sans-serif", marginTop: "8px" }}>
        Photon AI Portal
      </Text>
      <Text size={300} style={{ color: "rgba(255, 255, 255, 0.6)", marginTop: "-12px", fontFamily: "'Open Sans', sans-serif" }}>
        Confidential - For Authorized Use Only
      </Text>
      <Text size={400} style={{ color: "rgba(255, 255, 255, 0.8)", marginTop: "16px", fontFamily: "'Open Sans', sans-serif" }}>
        Sign in with your organization account to continue
      </Text>
      <button
        onClick={handleLogin}
        style={{
          padding: "14px 32px",
          fontSize: "16px",
          fontWeight: 600,
          fontFamily: "'Poppins', sans-serif",
          backgroundColor: "#63666A",
          color: "white",
          border: "none",
          borderRadius: "8px",
          cursor: "pointer",
          transition: "background-color 0.2s, transform 0.2s",
          boxShadow: "0 4px 12px rgba(99, 102, 106, 0.4)",
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.backgroundColor = "#4a4d50";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.backgroundColor = "#63666A";
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        Sign in with Microsoft
      </button>
      <Text size={200} style={{ color: "rgba(255, 255, 255, 0.5)", marginTop: "20px", fontFamily: "'Open Sans', sans-serif" }}>
        Internal Portal • Authorized Personnel Only
      </Text>
    </div>
  );
};

/**
 * Inner component that tracks login after authentication
 */
const AuthenticatedContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { trackLogin } = useUsageTracking();

  // Track login once per session when user is authenticated
  React.useEffect(() => {
    trackLogin();
  }, [trackLogin]);

  return <>{children}</>;
};

/**
 * Main Auth Provider
 * Handles automatic login and provides auth state to children
 */
/**
 * Auth gate used in production (MsalProvider present in tree).
 */
const MsalAuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { inProgress } = useMsal();

  // Show loading while MSAL is processing (redirect, popup, etc.)
  if (inProgress !== InteractionStatus.None) {
    return <AuthLoading />;
  }

  // MSAL is ready - show authenticated content or login prompt
  return (
    <>
      <AuthenticatedTemplate>
        <AuthenticatedContent>{children}</AuthenticatedContent>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginPrompt />
      </UnauthenticatedTemplate>
    </>
  );
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // In dev bypass mode there is no MsalProvider — skip the auth gate entirely.
  if (useDevBypass) {
    return <>{children}</>;
  }
  return <MsalAuthProvider>{children}</MsalAuthProvider>;
};

export default AuthProvider;
